import React, { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Result } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import useMaintenance from '../../hooks/useMaintenance';
import { getDateLocale } from '../../i18n';
import styles from './MaintenanceGate.module.less';

type MaintenanceGateProps = {
  children: ReactNode;
};

// Sterowanie z adresu, obie strony celowo symetryczne:
//   ?maintenance=1  wymusza ekran — podgląd wyglądu, także na produkcji
//   ?maintenance=0  pomija ekran — pozwala sprawdzić aplikację w trakcie przerwy
// Wartość jest odczytywana, nie sama obecność parametru: `has('maintenance')` byłoby
// prawdziwe również dla `=0`, czyli najbardziej naturalna próba obejścia dawałaby
// efekt odwrotny do zamierzonego.
type UrlOverride = 'on' | 'off' | null;

const BYPASS_STORAGE_KEY = 'maintenanceBypass';

// sessionStorage bywa niedostępny — tryb prywatny, zablokowane dane witryn. Przy błędzie
// NIE obchodzimy bramki: sama flaga działa fail-open, ale obejście jest fail-closed,
// bo pomyłka po tej stronie ukryłaby przed użytkownikiem prawdziwą przerwę.
const readBypass = (): boolean => {
  try {
    return window.sessionStorage.getItem(BYPASS_STORAGE_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const writeBypass = (enabled: boolean): void => {
  try {
    if (enabled) {
      window.sessionStorage.setItem(BYPASS_STORAGE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(BYPASS_STORAGE_KEY);
    }
  } catch (error) {
    // Brak pamięci sesji — obejście po prostu nie będzie pamiętane między kliknięciami.
  }
};

// Obejście trafia do sessionStorage, żeby przetrwało nawigację po aplikacji, ale zniknęło
// po zamknięciu karty. Gdyby siedziało w localStorage, można by za miesiąc nie zobaczyć
// prawdziwej przerwy i uznać, że flaga nie zadziałała.
const resolveUrlOverride = (): UrlOverride => {
  const param = new URLSearchParams(window.location.search).get('maintenance');

  if (param === '1') {
    writeBypass(false);

    return 'on';
  }

  if (param === '0') {
    writeBypass(true);

    return 'off';
  }

  return null;
};

const formatTime = (date: Date): string => date.toLocaleString(getDateLocale(), {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const MaintenanceGate: FC<MaintenanceGateProps> = ({ children }) => {
  const { t } = useTranslation();
  const {
    isActive, message, until, now,
  } = useMaintenance();

  // Inicjalizator useState wykonuje się raz, przed pierwszym malowaniem — dzięki temu
  // obejście działa natychmiast, bez mignięcia ekranu serwisowego.
  const [urlOverride] = useState<UrlOverride>(resolveUrlOverride);

  const isForced = urlOverride === 'on';
  const isBypassed = !isForced && readBypass();
  const showScreen = isForced || (isActive && !isBypassed);

  if (!showScreen) {
    return (
      <>
        {children}
        {/* Znacznik tylko wtedy, gdy realnie coś zasłaniasz — inaczej łatwo zapomnieć,
            że siedzisz w obejściu, i uznać, że przerwa się nie włączyła. */}
        {isActive && isBypassed && (
          <span className={styles.bypassBadge}>{t('common:maintenance.bypassBadge')}</span>
        )}
      </>
    );
  }

  // `until` informuje, nie odblokowuje — po minięciu terminu zmienia się tylko
  // komunikat. Automatyczne wygaszanie oznaczałoby powrót do pracy w chwili,
  // gdy aplikacja nadal jest zepsuta.
  const isOverdue = !!until && until.getTime() <= now.getTime();

  return (
    <div className={styles.gate}>
      <Result
        className={styles.result}
        icon={<ToolOutlined className={styles.icon} />}
        title={t('common:maintenance.title')}
        subTitle={message || t('common:maintenance.subtitle')}
        extra={(
          <>
            {until && (
              <p className={styles.until}>
                {isOverdue
                  ? t('common:maintenance.overdue')
                  : t('common:maintenance.until', { time: formatTime(until) })}
              </p>
            )}
            <Button type="primary" size="large" onClick={() => window.location.reload()}>
              {t('common:maintenance.refresh')}
            </Button>
          </>
        )}
      />
    </div>
  );
};

export default MaintenanceGate;
